import clsx from 'clsx';
import { ElementType, forwardRef, type ReactElement } from 'react';

import * as styles from './Avatar.sty.js';
import { Label } from './Label.js';
import { Subtitle } from './Subtitle.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { _sanitizeSymbol } from '../../core/utils/sanitize-symbol.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

const COMPONENT_NAME = 'Avatar';

// Merge own props with others inherited from the underlying element type
/** @public */
export type AvatarProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  AvatarOwnProps
>;

/**
 * Accepted properties for the Avatar component
 * @public
 */
export interface AvatarOwnProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The size of the Avatar.
   * @defaultValue 'default'
   */
  size?: 'default' | 'small';

  /**
   * The letters displayed in the colored circle of the Avatar.
   */
  abbreviation: string;

  /**
   * Whether the Avatar is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
}

/**
 * Avatar component
 */
const InternalAvatar: <E extends ElementType = 'div'>(
  props: AvatarProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(props: AvatarProps<E>, ref: typeof props.ref) => {
    const {
      children,
      size = 'default',
      abbreviation,
      disabled = false,
      as,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const [slots] = useSlots(children, {
      label: Label,
      subtitle: Subtitle,
    });

    const Abbreviation = as ?? 'div';

    // ensuring that 'rovingFocusElement' remains assigned to the Abbrevation Element for focus functionality.
    const remainingConsumerClassname = (consumerClassName ?? '').replace(
      /rovingFocusElement/g,
      '',
    );
    const hasRovingFocus = (consumerClassName ?? '').includes(
      'rovingFocusElement',
    );
    return (
      <div
        className={clsx(
          styles.avatarWrapper,
          textStyleCSS({ textStyle: 'base' }),
          remainingConsumerClassname,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Flex gap={size === 'small' ? 4 : 8} alignItems="center" minWidth={0}>
          <Abbreviation
            ref={ref}
            className={clsx(
              {
                [styles.avatarDisabled]: disabled,
                rovingFocusElement: hasRovingFocus,
              },
              styles.avatarAbbreviation({ size, disabled }),
              focusRingStatic({ disabled, when: 'focus-within-visible' }),
            )}
            {...ariaLabelingProps}
            {...forwardProps}
          >
            {_sanitizeSymbol(abbreviation, size === 'default' ? 2 : undefined)}
          </Abbreviation>
          {(slots.label || slots.subtitle) && (
            <Flex flexDirection="column" gap={0} minWidth={0}>
              {slots.label}
              {slots.subtitle}
            </Flex>
          )}
        </Flex>
      </div>
    );
  },
);

(
  InternalAvatar as typeof InternalAvatar & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Use the `Avatar` to display a visual representation of a user, showing their profile picture, initials, or a placeholder icon.
 * @public
 */
export const Avatar = Object.assign(InternalAvatar, {
  Label,
  Subtitle,
});
